<script lang="ts">
    import type { ClassValue } from "svelte/elements";
    import * as ButtonGroup from "./ui/button-group/index.js";
    import Button from "./ui/button/button.svelte";
    import ListFilterIcon from "@lucide/svelte/icons/list-filter";
    import XIcon from "@lucide/svelte/icons/x";
    import * as DropdownMenu from "./ui/dropdown-menu/index.js";
    import { assert, keyBy, keyOrDie, type Condition } from "@repo/utils";
    import Unreachable from "./common/unreachable.svelte";
    import Throw from "./common/throw.svelte";
    import * as Dialog from "./ui/dialog/index.js";
    import { Input } from "./ui/input/index.js";

    let {
        value = $bindable(),
        config,
        class: className,
    }: {
        value: Condition | null | undefined;
        config: {
            fields: ({ key: string; title: string } & (
                | { type: "options"; options: { key: string; title: string }[] }
                | { type: "text" }
                | { type: "date" }
                | { type: "array" }
            ))[];
        };
        class: ClassValue;
    } = $props();
    const fieldsByKey = $derived(keyBy(config.fields, (x) => x.key));
    let editingCondition = $state<Condition | null>(null);
    let editingIndex = $state<number | null>(null);

    function closeDialog() {
        editingCondition = null;
        editingIndex = null;
    }
</script>

<ButtonGroup.Root class={className}>
    {#if value?.op === "and"}
        {#each value.value.filter((x) => "field" in x) as c, i (`${c.op}${i}`)}
            {@const field = keyOrDie(fieldsByKey, c.field)}
            <ButtonGroup.Root>
                <Button size="sm" variant="outline">{field.title}</Button>
                {#if c.op === "anyOf"}
                    {#if field.type === "options"}
                        <Button size="sm" variant="outline">{c.value.length === 1 ? "is" : "is any of"}</Button>
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger>
                                {#snippet child({ props })}
                                    <Button {...props} size="sm" variant="outline">
                                        {c.value
                                            .map((x) => field.options.find((y) => y.key === x)?.title ?? x)
                                            .join(", ")}
                                    </Button>
                                {/snippet}
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content>
                                <DropdownMenu.Group>
                                    {#each field.options as o (o.key)}
                                        <DropdownMenu.CheckboxItem
                                            checked={c.value.includes(o.key)}
                                            onCheckedChange={(x) => {
                                                if (x) {
                                                    c.value.push(o.key);
                                                } else {
                                                    c.value.splice(c.value.indexOf(o.key), 1);
                                                }
                                            }}
                                        >
                                            {o.title}
                                        </DropdownMenu.CheckboxItem>
                                    {/each}
                                </DropdownMenu.Group>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    {:else}
                        <Throw error={new Error(`Field ${field.type} is not supported for this operation`)} />
                    {/if}
                {:else if c.op === "like" || c.op === "ilike"}
                    <Button size="sm" variant="outline">contains</Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onclick={() => {
                            editingCondition = { ...c };
                            editingIndex = i;
                        }}
                    >
                        {c.value}
                    </Button>
                {:else if c.op === "contains"}
                    <Button size="sm" variant="outline">contains</Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onclick={() => {
                            editingCondition = { ...c };
                            editingIndex = i;
                        }}
                    >
                        {c.value}
                    </Button>
                {/if}
                <Button
                    size="icon-sm"
                    variant="outline"
                    onclick={() => {
                        value.value.splice(i, 1);
                    }}
                >
                    <XIcon />
                </Button>
            </ButtonGroup.Root>
        {/each}
    {/if}
    <ButtonGroup.Root>
        <DropdownMenu.Root>
            <DropdownMenu.Trigger>
                {#snippet child({ props })}
                    <Button {...props} size="icon-sm" variant="ghost">
                        <ListFilterIcon />
                    </Button>
                {/snippet}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
                <DropdownMenu.Group>
                    {#each config.fields as f (f.key)}
                        {#if f.type === "options"}
                            <DropdownMenu.Sub>
                                <DropdownMenu.SubTrigger>{f.title}</DropdownMenu.SubTrigger>
                                <DropdownMenu.SubContent>
                                    {#each f.options as o (o.key)}
                                        <DropdownMenu.CheckboxItem
                                            onCheckedChange={() => {
                                                assert(value?.op === "and");
                                                value.value.push({
                                                    op: "anyOf",
                                                    field: f.key,
                                                    value: [o.key],
                                                });
                                            }}
                                        >
                                            {o.title}
                                        </DropdownMenu.CheckboxItem>
                                    {/each}
                                </DropdownMenu.SubContent>
                            </DropdownMenu.Sub>
                        {:else if f.type === "text"}
                            <DropdownMenu.Item
                                onSelect={() => {
                                    editingCondition = {
                                        op: "ilike",
                                        field: f.key,
                                        value: "",
                                    };
                                }}
                            >
                                {f.title}
                            </DropdownMenu.Item>
                        {:else if f.type === "date"}
                            <DropdownMenu.Sub>
                                <DropdownMenu.SubTrigger>
                                    {f.title}
                                </DropdownMenu.SubTrigger>
                                <DropdownMenu.SubContent>
                                    <DropdownMenu.Item>1 day ago</DropdownMenu.Item>
                                    <DropdownMenu.Item>3 days ago</DropdownMenu.Item>
                                    <DropdownMenu.Item>1 week ago</DropdownMenu.Item>
                                </DropdownMenu.SubContent>
                            </DropdownMenu.Sub>
                        {:else if f.type === "array"}
                            <DropdownMenu.Item
                                onSelect={() => {
                                    editingCondition = {
                                        op: "contains",
                                        field: f.key,
                                        value: "",
                                    };
                                }}
                            >
                                {f.title}
                            </DropdownMenu.Item>
                        {:else}
                            <Unreachable value={f} />
                        {/if}
                    {/each}
                </DropdownMenu.Group>
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    </ButtonGroup.Root>
</ButtonGroup.Root>

<Dialog.Root
    bind:open={
        () => editingCondition !== null && "field" in editingCondition,
        (v) => {
            if (!v) {
                closeDialog();
            }
        }
    }
>
    <Dialog.Content>
        {#if editingCondition && "field" in editingCondition}
            {@const field = keyOrDie(fieldsByKey, editingCondition.field)}
            {@const e = editingCondition}
            <Dialog.Header>
                <Dialog.Title>Filter by {field.title}</Dialog.Title>
            </Dialog.Header>
            <Input
                bind:value={
                    () => e.value,
                    (x) => {
                        e.value = x ?? "";
                    }
                }
            />
            <Dialog.Footer>
                <Dialog.Close>
                    <Button variant="secondary" onclick={closeDialog}>Cancel</Button>
                </Dialog.Close>
                <Button
                    onclick={() => {
                        assert(value?.op === "and");
                        if (editingIndex === null) {
                            value.value.push(e);
                        } else {
                            value.value[editingIndex] = e;
                        }
                        closeDialog();
                    }}
                >
                    Apply
                </Button>
            </Dialog.Footer>
        {/if}
    </Dialog.Content>
</Dialog.Root>
